// BlobViewerDialog.tsx
import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Input,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import mammoth from "mammoth";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import * as pdfjsLib from "pdfjs-dist";
import * as UTIF from "utif";

import API from "../api/axiosInstance";

interface BlobViewerDialogProps {
  open: boolean;
  onClose: () => void;
  fileID: string;
  fieldLabel?: string;
  mimeType?: string;
  isBlob?: boolean;
}

const BlobViewerDialog: React.FC<BlobViewerDialogProps> = ({
  open,
  onClose,
  fileID,
  fieldLabel = "File",
  mimeType,
  isBlob = false,
}) => {
  const [blobUrl, setBlobUrl] = useState("");
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [resolvedMimeType, setResolvedMimeType] = useState<string | null>(mimeType ?? null);
  const [showInfo, setShowInfo] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [highlightedHtml, setHighlightedHtml] = useState("");
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [zoom, setZoom] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (fileID && open) fetchBlobFromAPI(fileID);
  }, [fileID, open, pageIndex]);

  const fetchBlobFromAPI = async (id: string) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || "{}");
      const headers = {
        headers: {
          authToken: user.user?.authtoken,
          sessionId: user.user?.sessionId,
        },
        responseType: "blob",
      };

      const res = await API.get(`/storage/${isBlob ? "blob" : "file"}/${id}`, headers);
      const blob = res.data;
      setResolvedMimeType(blob.type);
      setFileSize(blob.size);

      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        decodeBlob(dataUrl, blob.type);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error("Failed to load blob from API:", err);
    }
  };

  const decodeBlob = async (dataUrl: string, type: string) => {
    try {
      const byteString = atob(dataUrl.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);

      if (type.startsWith("text") || fieldLabel.endsWith(".txt")) {
        const text = await blob.text();
        setFileContent(text);
      } else if (type.includes("wordprocessingml.document")) {
        const buffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
        setFileContent(result.value);
      } else if (type.includes("spreadsheetml")) {
        const buffer = await blob.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[workbook.SheetNames[0]]);
        const parsed = Papa.parse(csv).data as string[][];
        setCsvData(parsed);
      } else if (type.includes("tiff")) {
        const buffer = await blob.arrayBuffer();
        const ifds = UTIF.decode(buffer);
        const selectedIFD = ifds[pageIndex] || ifds[0];
        UTIF.decodeImage(buffer, selectedIFD);
        const rgba = UTIF.toRGBA8(selectedIFD);
        const { width, height } = selectedIFD;
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const imageData = ctx.createImageData(width, height);
            imageData.data.set(rgba);
            ctx.putImageData(imageData, 0, 0);
          }
        }
      } else if (type === "application/pdf") {
        const pdf = await pdfjsLib.getDocument({ url }).promise;
        setTotalPages(pdf.numPages);
      }
    } catch (err) {
      console.error("Failed to decode blob:", err);
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fieldLabel;
    a.click();
  };

  const handlePrint = () => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => iframe.contentWindow?.print();
  };

  const handleSearch = () => {
    if (fileContent && searchTerm) {
      const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(escaped, "gi");
      const highlighted = fileContent.replace(re, (match) => `<mark>${match}</mark>`);
      setHighlightedHtml(highlighted);
    }
  };

  const renderViewer = () => {
    if (!blobUrl) {
      return (
        <Box p={3}>
          <Typography variant="body2" color="error">
            Unable to load file.
          </Typography>
        </Box>
      );
    }

    if (resolvedMimeType?.startsWith("image/") && !resolvedMimeType.includes("tiff")) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
          <img
            src={blobUrl}
            alt={fieldLabel}
            style={{ transform: `scale(${zoom})`, maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>
      );
    }

    if (resolvedMimeType?.includes("tiff")) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
          <canvas ref={canvasRef} style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </Box>
      );
    }

    if (resolvedMimeType === "application/pdf") {
      return (
        <iframe
          src={blobUrl}
          title="PDF Viewer"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      );
    }

    if (resolvedMimeType?.includes("wordprocessingml.document")) {
      return (
        <Box
          sx={{ p: 2, overflowY: "auto", maxHeight: "calc(100vh - 160px)" }}
          dangerouslySetInnerHTML={{ __html: highlightedHtml || fileContent }}
        />
      );
    }

    if (resolvedMimeType?.includes("spreadsheetml") || fieldLabel.endsWith(".csv")) {
      return (
        <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 160px)", p: 2 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {csvData[0]?.map((cell, idx) => (
                  <th key={idx} style={{ border: "1px solid #ccc", padding: "4px" }}>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {csvData.slice(1).map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} style={{ border: "1px solid #ccc", padding: "4px" }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    }

    if (resolvedMimeType?.startsWith("text")) {
      return (
        <Box
          sx={{
            p: 2,
            whiteSpace: "pre-wrap",
            fontFamily: "monospace",
            overflowY: "auto",
            maxHeight: "calc(100vh - 160px)",
          }}
        >
          <Typography component="div" dangerouslySetInnerHTML={{ __html: highlightedHtml || fileContent }} />
        </Box>
      );
    }

    return (
      <Box p={3}>
        <Typography variant="body2" color="warning.main">
          Unsupported file type for preview.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">{fieldLabel}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {fileContent && (
            <>
              <TextField
                size="small"
                placeholder="Search text..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 200 }}
              />
              <Button onClick={handleSearch} variant="outlined" size="small">
                Search
              </Button>
            </>
          )}
          {(resolvedMimeType?.startsWith("image/") || resolvedMimeType?.includes("tiff")) && (
            <>
              <Tooltip title="Zoom In">
                <IconButton onClick={() => setZoom((z) => Math.min(z + 0.25, 3))}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom Out">
                <IconButton onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {(resolvedMimeType === "application/pdf" || resolvedMimeType?.includes("tiff")) &&
            totalPages > 1 && (
              <>
                <Tooltip title="Previous Page">
                  <IconButton onClick={() => setPageIndex((p) => Math.max(p - 1, 0))}>
                    <NavigateBeforeIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2">Page {pageIndex + 1} of {totalPages}</Typography>
                <Tooltip title="Next Page">
                  <IconButton onClick={() => setPageIndex((p) => Math.min(p + 1, totalPages - 1))}>
                    <NavigateNextIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          <Tooltip title="File Info">
            <IconButton onClick={() => setShowInfo(true)}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>{renderViewer()}</DialogContent>

      <Dialog open={showInfo} onClose={() => setShowInfo(false)}>
        <DialogTitle>File Info</DialogTitle>
        <DialogContent>
          <Typography><strong>Label:</strong> {fieldLabel}</Typography>
          <Typography><strong>Type:</strong> {resolvedMimeType}</Typography>
          <Typography><strong>Size:</strong> {fileSize?.toLocaleString()} bytes</Typography>
          {totalPages > 1 && (
            <Typography><strong>Pages:</strong> {totalPages}</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default BlobViewerDialog;
